import { MAP_FILTER_SLOW_MAX_KW } from './evuaMapFilters';

const CACHE_STORAGE_KEY = 'evuaStationKwCache.v1';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_EVUA_KW = 80;
const DEFAULT_EVUA_AC_KW = 22;

function readStore() {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_e) {
    return {};
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(store));
  } catch (_e) {
    /* quota / private mode */
  }
}

function pruneStore(store, now = Date.now()) {
  Object.keys(store).forEach(key => {
    const entry = store[key];
    if (!entry || !Number.isFinite(entry.savedAt) || now - entry.savedAt > CACHE_TTL_MS) {
      delete store[key];
    }
  });
  return store;
}

/** Cached kW from an already-loaded store; null when missing/expired. */
function readStoredKw(store, stationKey, now) {
  if (stationKey == null || stationKey === '') return null;
  const entry = store[String(stationKey)];
  if (!entry || !Number.isFinite(entry.savedAt) || now - entry.savedAt > CACHE_TTL_MS) {
    return null;
  }
  const kw = Number(entry.powerKw);
  return Number.isFinite(kw) && kw > 0 ? kw : null;
}

/** Stage kW into an in-memory store; true when the store changed and needs persisting. */
function writeStoredKw(store, stationKey, powerKw, now) {
  if (stationKey == null || stationKey === '') return false;
  const kw = Number(powerKw);
  if (!Number.isFinite(kw) || kw <= 0) return false;
  store[String(stationKey)] = { savedAt: now, powerKw: kw };
  return true;
}

/** Read cached station power (kW); null when missing/expired. */
export function getCachedEvuaKw(stationKey) {
  if (stationKey == null || stationKey === '') return null;
  return readStoredKw(readStore(), stationKey, Date.now());
}

/** Persist station power (kW) for one day. */
export function setCachedEvuaKw(stationKey, powerKw) {
  if (stationKey == null || stationKey === '') return;
  const kw = Number(powerKw);
  if (!Number.isFinite(kw) || kw <= 0) return;
  const now = Date.now();
  const store = pruneStore(readStore(), now);
  writeStoredKw(store, stationKey, kw, now);
  writeStore(store);
}

/** Rated power in kW from EV UA API fields {@code maxpow}/{@code minpow}. */
export function getEvuaApiPowerKw(port, { preferMin = false } = {}) {
  const maxpow = Number(port?.maxpow);
  const minpow = Number(port?.minpow);
  const maxOk = Number.isFinite(maxpow) && maxpow > 0;
  const minOk = Number.isFinite(minpow) && minpow > 0;
  if (preferMin) {
    if (minOk && minpow < MAP_FILTER_SLOW_MAX_KW) return minpow;
    if (maxOk && maxpow < MAP_FILTER_SLOW_MAX_KW) return maxpow;
    return null;
  }
  if (maxOk) return maxpow;
  if (minOk) return minpow;
  return null;
}

/**
 * Single source of truth for the kW precedence, working against an in-memory store so callers
 * decide when to hit localStorage.
 * @returns {{ powerKw: number, approximate: boolean, stored: boolean }} `stored` marks the store dirty.
 */
function resolveAgainstStore(
  port,
  { matchKw, fallbackKw, store, now, ignoreExplicitWatts = false, preferMinPower = false }
) {
  const stationKey = port?.key ?? port?.number;

  if (!ignoreExplicitWatts) {
    const watts = Number(port?.maxPowerWt ?? port?.max_power_wt);
    if (Number.isFinite(watts) && watts > 0) {
      const powerKw = watts / 1000;
      return { powerKw, approximate: false, stored: writeStoredKw(store, stationKey, powerKw, now) };
    }
  }

  const apiKw = getEvuaApiPowerKw(port, { preferMin: preferMinPower });
  if (apiKw != null) {
    return { powerKw: apiKw, approximate: false, stored: writeStoredKw(store, stationKey, apiKw, now) };
  }

  if (!preferMinPower) {
    const matched = Number(matchKw);
    if (Number.isFinite(matched) && matched > 0) {
      return { powerKw: matched, approximate: false, stored: writeStoredKw(store, stationKey, matched, now) };
    }

    const cached = readStoredKw(store, stationKey, now);
    if (cached != null) {
      return { powerKw: cached, approximate: false, stored: false };
    }
  }

  return { powerKw: fallbackKw, approximate: true, stored: false };
}

/**
 * Resolve EV UA station power for map clusters:
 * explicit watts → EV UA maxpow/minpow → matched 220-km kW (also cached) → 1-day cache → default 80 kW.
 * Slow (`preferMinPower`) uses minpow and skips DC match/cache so AC pins stay AC-sized.
 * `approximate` is true only for the default fallback (EV UA API has no kW).
 */
export function resolveEvuaPowerKwDetails(
  port,
  { matchKw = null, fallbackKw = DEFAULT_EVUA_KW, ignoreExplicitWatts = false, preferMinPower = false } = {}
) {
  const now = Date.now();
  const store = pruneStore(readStore(), now);
  const { powerKw, approximate, stored } = resolveAgainstStore(port, {
    matchKw,
    fallbackKw,
    store,
    now,
    ignoreExplicitWatts,
    preferMinPower,
  });
  if (stored) {
    writeStore(store);
  }
  return { powerKw, approximate };
}

/**
 * Same precedence as {@link resolveEvuaPowerKwDetails} for a whole list, but touching localStorage
 * once instead of once per station (the map resolves ~1.5k EV UA stations per index rebuild).
 * @param {object[]} ports
 * @param {{
 *   getMatchKw?: (port: object, index: number) => number|null|undefined,
 *   fallbackKw?: number,
 *   ignoreExplicitWatts?: boolean,
 *   preferMinPower?: boolean,
 * }} [opts] `ignoreExplicitWatts` skips the `maxPowerWt` step for callers that re-inject it themselves.
 * @returns {{ powerKw: number, approximate: boolean }[]} aligned with `ports`
 */
export function resolveEvuaPowerKwDetailsBatch(
  ports,
  { getMatchKw = null, fallbackKw = DEFAULT_EVUA_KW, ignoreExplicitWatts = false, preferMinPower = false } = {}
) {
  if (!Array.isArray(ports) || ports.length === 0) {
    return [];
  }
  const now = Date.now();
  const store = pruneStore(readStore(), now);
  let dirty = false;

  const results = ports.map((port, index) => {
    const matchKw = getMatchKw ? getMatchKw(port, index) : null;
    const { powerKw, approximate, stored } = resolveAgainstStore(port, {
      matchKw,
      fallbackKw,
      store,
      now,
      ignoreExplicitWatts,
      preferMinPower,
    });
    dirty = dirty || stored;
    return { powerKw, approximate };
  });

  if (dirty) {
    writeStore(store);
  }
  return results;
}

export function resolveEvuaPowerKw(port, options) {
  return resolveEvuaPowerKwDetails(port, options).powerKw;
}

export const EVUA_DEFAULT_POWER_KW = DEFAULT_EVUA_KW;
export const EVUA_DEFAULT_AC_POWER_KW = DEFAULT_EVUA_AC_KW;
